import { useCCValues, useCCHistory, useNoteFlashes } from '../hooks/use-oddball-events';
import type { CCValues } from '../hooks/use-oddball-events';

const CC_LABELS: { key: keyof CCValues; label: string; color: string }[] = [
  { key: 'move', label: 'Move', color: '#4a7' },
  { key: 'spin', label: 'Spin', color: '#7a4' },
  { key: 'freeFall', label: 'FreeFall', color: '#a74' },
  { key: 'orientX', label: 'Orient X', color: '#47a' },
  { key: 'orientY', label: 'Orient Y', color: '#74a' },
  { key: 'orientZ', label: 'Orient Z', color: '#a47' },
  { key: 'energy', label: 'Energy', color: '#aa4' },
];

const GESTURE_COLORS: Record<string, string> = {
  tap: '#ffb432',
  shake: '#508cff',
  twist: '#b450ff',
};

function Sparkline({ data, color, width = 120, height = 24 }: { data: number[]; color: string; width?: number; height?: number }) {
  if (data.length < 2) return <div style={{ width, height }} />;

  const max = 127;
  const points = data
    .map((v, i) => {
      const x = (i / (data.length - 1)) * width;
      const y = height - (v / max) * height;
      return `${x},${y}`;
    })
    .join(' ');

  return (
    <svg width={width} height={height} style={{ display: 'block' }}>
      <polyline points={points} fill="none" stroke={color} stroke-width="1.5" opacity="0.7" />
    </svg>
  );
}

export function SensorDashboard() {
  const cc = useCCValues();
  const history = useCCHistory(90);
  const flash = useNoteFlashes();

  return (
    <div>
      <h2 style={{ fontSize: '1rem', fontWeight: 300, marginBottom: '16px', color: '#888' }}>
        Sensor Dashboard
      </h2>

      {/* CC Bars */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {CC_LABELS.map(({ key, label, color }) => (
          <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ width: '70px', fontSize: '0.75rem', color: '#888', textAlign: 'right' }}>
              {label}
            </span>
            <span style={{ width: '30px', fontSize: '0.75rem', color, fontFamily: 'monospace', textAlign: 'right' }}>
              {cc[key]}
            </span>
            <div
              style={{
                flex: 1,
                maxWidth: '200px',
                height: '12px',
                background: '#1a1a1a',
                borderRadius: '3px',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: (cc[key] / 127) * 100 + '%',
                  height: '100%',
                  background: color,
                  opacity: 0.7,
                  borderRadius: '3px',
                  transition: 'width 0.05s',
                }}
              />
            </div>
            <Sparkline data={history[key]} color={color} />
          </div>
        ))}
      </div>

      {/* Note flashes */}
      <div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
        {(['tap', 'shake', 'twist'] as const).map((type) => {
          const isActive = flash && flash.type === type && performance.now() - flash.timestamp < 300;
          const color = GESTURE_COLORS[type];
          return (
            <div
              key={type}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                background: isActive ? color : '#1a1a1a',
                color: isActive ? '#111' : '#555',
                fontSize: '0.8rem',
                fontWeight: 600,
                transition: 'all 0.1s',
                textTransform: 'uppercase',
              }}
            >
              {type}
              {isActive && flash && (
                <span style={{ marginLeft: '6px', fontWeight: 400 }}>
                  v{flash.velocity}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
